/**
 * DocumentHubTab — P0.18.2 Operational Document Hub (Aug 2026).
 *
 * Centraal Document Center dat ALLE operationele documenten aggregeert
 * uit shipments, consolidaties, quotes, invoices, packing lists en
 * booking forms. Ondersteunt zoeken, filters op type/status/klant,
 * bulk-download (ZIP) en bulk-email.
 *
 * Data testids:
 *   document-hub-tab
 *   document-hub-search
 *   document-hub-type-filter
 *   document-hub-status-filter
 *   document-hub-item-{doc.id}
 *   document-hub-bulk-download
 *   document-hub-bulk-email
 *   document-hub-select-all
 *   document-hub-select-{doc.id}
 *   document-hub-download-{doc.id}
 *   document-hub-open-shipment-{doc.id}
 */
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  FileText, Search, Filter, Download, Mail, ExternalLink, Loader2,
  Ship, Plane, FileSpreadsheet, Receipt, ClipboardList, Package,
  BookOpen, CheckSquare, Square, RefreshCw, ChevronLeft, ChevronRight,
  Eye, RotateCcw, Send,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { useGlobalLoading } from "../../../context/GlobalLoadingProvider";
import DocumentPreviewModal from "./DocumentPreviewModal";
import BulkEmailPreviewDialog from "./BulkEmailPreviewDialog";
import ShipmentLookupDialog from "./ShipmentLookupDialog";

const TYPE_ICONS = {
  warehouse_receipt: <Package className="w-3.5 h-3.5" />,
  shipping_label: <Package className="w-3.5 h-3.5" />,
  packing_list: <ClipboardList className="w-3.5 h-3.5" />,
  booking_form: <BookOpen className="w-3.5 h-3.5" />,
  quote: <FileText className="w-3.5 h-3.5" />,
  invoice: <Receipt className="w-3.5 h-3.5" />,
  manifest_sea: <Ship className="w-3.5 h-3.5" />,
  manifest_air: <Plane className="w-3.5 h-3.5" />,
  manifest_xlsx: <FileSpreadsheet className="w-3.5 h-3.5" />,
  manifest_csv: <FileText className="w-3.5 h-3.5" />,
  manifest_json: <FileText className="w-3.5 h-3.5" />,
  manifest_xml: <FileText className="w-3.5 h-3.5" />,
};

const TYPE_COLORS = {
  warehouse_receipt: "text-amber-700 bg-amber-50",
  shipping_label: "text-blue-700 bg-blue-50",
  packing_list: "text-emerald-700 bg-emerald-50",
  booking_form: "text-purple-700 bg-purple-50",
  quote: "text-indigo-700 bg-indigo-50",
  invoice: "text-rose-700 bg-rose-50",
  manifest_sea: "text-cyan-800 bg-cyan-50",
  manifest_air: "text-sky-700 bg-sky-50",
  manifest_xlsx: "text-emerald-700 bg-emerald-50",
  manifest_csv: "text-gray-700 bg-gray-50",
  manifest_json: "text-orange-700 bg-orange-50",
  manifest_xml: "text-violet-700 bg-violet-50",
};

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      dateStyle: "short", timeStyle: "short",
    });
  } catch { return iso; }
}

export default function DocumentHubTab() {
  const { user, hasPermission } = useAuth();
  const { runTask } = useGlobalLoading();
  const canSend = hasPermission?.("documents:send") || false;
  const canSendBulk = hasPermission?.("documents:send_bulk") || false;
  const canRegenerate = hasPermission?.("documents:regenerate") || false;
  const canPreview = hasPermission?.("documents:preview") || false;
  const canLookupShipment = hasPermission?.("shipments:lookup") ?? true;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeLabels, setTypeLabels] = useState({});
  const [typesAvailable, setTypesAvailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = useState(0);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);
  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (selectedTypes.length) params.set("types", selectedTypes.join(","));
      if (statusFilter) params.set("status", statusFilter);
      params.set("limit", String(pageSize));
      params.set("offset", String(page * pageSize));
      const { data } = await adminApi.get(`/admin/document-hub?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTypeLabels(data.type_labels || {});
      setTypesAvailable(data.types_available || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Documenten laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [q, selectedTypes, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const toggleType = (t) => {
    setPage(0);
    setSelectedTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };

  const handleDownloadOne = async (doc) => {
    try {
      await runTask({
        id: `dh-download-${doc.id}`,
        message: `Bezig met ${doc.type_label} downloaden…`,
        successMessage: `${doc.type_label} gedownload`,
        run: async () => {
          const resp = await adminApi.get(doc.pdf_url.replace(/^\/api/, ""), {
            responseType: "blob",
          });
          const url = window.URL.createObjectURL(new Blob([resp.data]));
          const a = document.createElement("a");
          a.href = url;
          let filename = `${doc.type}-${doc.number}`;
          const disp = resp.headers["content-disposition"] || "";
          const m = disp.match(/filename="?([^";]+)"?/);
          if (m) filename = m[1];
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        },
      });
    } catch (e) {
      if (e?.message !== "DUPLICATE_TASK") {
        toast.error(formatApiError(e) || "Download mislukt");
      }
    }
  };

  const handleBulkDownload = async () => {
    if (selectedIds.size === 0) {
      toast.error("Selecteer eerst één of meer documenten");
      return;
    }
    try {
      await runTask({
        id: "dh-bulk-download",
        message: `ZIP-bestand wordt samengesteld (${selectedIds.size} documenten)…`,
        successMessage: `${selectedIds.size} documenten in ZIP`,
        run: async () => {
          const resp = await adminApi.post(
            "/admin/document-hub/bulk-download",
            { document_ids: Array.from(selectedIds) },
            { responseType: "blob" }
          );
          const url = window.URL.createObjectURL(new Blob([resp.data]));
          const a = document.createElement("a");
          a.href = url;
          const disp = resp.headers["content-disposition"] || "";
          const m = disp.match(/filename="?([^";]+)"?/);
          a.download = m ? m[1] : "5999cargo-documenten.zip";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          setSelectedIds(new Set());
        },
      });
    } catch (e) {
      if (e?.message !== "DUPLICATE_TASK") {
        toast.error(formatApiError(e) || "Bulk-download mislukt");
      }
    }
  };

  const handleBulkEmail = () => {
    if (selectedIds.size === 0) {
      toast.error("Selecteer eerst één of meer documenten");
      return;
    }
    // Open preview dialog i.p.v. direct verzenden
    setShowBulkEmailDialog(true);
  };

  const handleRegenerate = async (doc) => {
    try {
      await runTask({
        id: `dh-regen-${doc.id}`,
        message: "Document wordt opnieuw gegenereerd…",
        successMessage: `${doc.type_label} succesvol opnieuw gegenereerd`,
        run: async () => {
          await adminApi.post(
            `/admin/document-hub/${encodeURIComponent(doc.id)}/regenerate`
          );
          load();
        },
      });
    } catch (e) {
      if (e?.message !== "DUPLICATE_TASK") {
        toast.error(formatApiError(e) || "Regeneratie mislukt");
      }
    }
  };

  const handleResend = async (doc) => {
    if (!window.confirm(`Document ${doc.number} opnieuw verzenden naar de gekoppelde ontvanger?`)) return;
    try {
      await runTask({
        id: `dh-resend-${doc.id}`,
        message: "Document wordt opnieuw verzonden…",
        successMessage: "Verzoek verstuurd",
        run: async () => {
          const { data } = await adminApi.post(
            `/admin/document-hub/${encodeURIComponent(doc.id)}/resend`
          );
          if (data.status === "sent") {
            toast.success(`Verzonden naar ${data.recipient}`);
          } else if (data.status === "skipped_missing_recipient") {
            toast.warning("Overgeslagen — geen ontvangeradres");
          } else if (data.status === "failed") {
            toast.error(`Mislukt: ${data.error || "onbekende fout"}`);
          } else if (data.status === "retry_pending") {
            toast.info("Tijdelijke fout — automatisch opnieuw geprobeerd");
          }
          load();
        },
      });
    } catch (e) {
      if (e?.message !== "DUPLICATE_TASK") {
        toast.error(formatApiError(e) || "Verzenden mislukt");
      }
    }
  };

  const openShipment = (doc) => {
    if (doc.shipment_id) {
      // Best-effort — we openen via section=warehouse&w=... waar mogelijk
      const url = doc.w_number
        ? `/admin?section=warehouse&w=${doc.w_number}`
        : `/admin?section=warehouse`;
      window.open(url, "_blank");
    } else if (doc.consolidation_id) {
      window.open(
        `/admin?section=consolidaties&cid=${doc.consolidation_id}`,
        "_blank"
      );
    } else {
      toast.info("Geen gekoppelde zending of consolidatie");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-3" data-testid="document-hub-tab">
      {/* Zoek + filters */}
      <div className="bg-white border border-gray-200 p-3">
        <div className="flex flex-col md:flex-row gap-2 mb-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(0); }}
              placeholder="Zoek op W-nummer, klantnaam, documentnummer, factuurnr, offertenr, consolidatienr..."
              className="pl-8 rounded-none"
              data-testid="document-hub-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="rounded-none border border-gray-300 px-2 text-sm"
            data-testid="document-hub-status-filter"
          >
            <option value="">Alle statussen</option>
            <option value="concept">Concept</option>
            <option value="sent">Verzonden</option>
            <option value="paid">Betaald</option>
            <option value="unpaid">Openstaand</option>
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            className="rounded-none"
            data-testid="document-hub-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          {canLookupShipment && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setLookupOpen(true)}
              className="rounded-none text-xs border-[#082c50] text-[#082c50] hover:bg-[#082c50] hover:text-white"
              data-testid="document-hub-shipment-lookup"
            >
              <Search className="w-3.5 h-3.5 mr-1" /> Zending opzoeken
            </Button>
          )}
        </div>

        {/* Type chips filter */}
        <div className="flex flex-wrap gap-1" data-testid="document-hub-type-filter">
          {typesAvailable.map((t) => {
            const active = selectedTypes.includes(t);
            return (
              <button
                key={t}
                onClick={() => toggleType(t)}
                className={`text-[10px] uppercase font-bold tracking-wide px-2 py-1 border transition-colors ${
                  active
                    ? "bg-[#0055FF] text-white border-[#0055FF]"
                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-500"
                }`}
                data-testid={`document-hub-chip-${t}`}
              >
                {TYPE_ICONS[t]} <span className="ml-1">{typeLabels[t] || t}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between bg-[#0055FF]/5 border border-[#0055FF]/30 px-3 py-2"
          data-testid="document-hub-bulk-bar"
        >
          <div className="text-xs text-[#082c50] font-bold">
            {selectedIds.size} geselecteerd
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkDownload}
              className="rounded-none text-xs"
              data-testid="document-hub-bulk-download"
            >
              <Download className="w-3 h-3 mr-1" /> ZIP downloaden
            </Button>
            {canSendBulk && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleBulkEmail}
                className="rounded-none text-xs"
                data-testid="document-hub-bulk-email"
              >
                <Mail className="w-3 h-3 mr-1" /> Bulk-e-mail
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
              className="rounded-none text-xs"
            >
              Selectie leegmaken
            </Button>
          </div>
        </div>
      )}

      {/* Lijst */}
      <div className="bg-white border border-gray-200">
        <div className="grid grid-cols-12 items-center px-3 py-2 bg-gray-50 border-b border-gray-200 text-[10px] uppercase font-bold text-gray-500 tracking-wide">
          <div className="col-span-1">
            <button
              onClick={selectAll}
              className="text-[#082c50]"
              data-testid="document-hub-select-all"
            >
              {selectedIds.size === items.length && items.length > 0
                ? <CheckSquare className="w-4 h-4" />
                : <Square className="w-4 h-4" />}
            </button>
          </div>
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Titel</div>
          <div className="col-span-2">Klant</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-1 text-right">Aangemaakt</div>
          <div className="col-span-2 text-right">Acties</div>
        </div>

        {loading ? (
          <div className="p-10 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#082c50]" />
            <p className="text-sm text-gray-500 mt-2">Documenten worden geladen…</p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            Geen documenten gevonden voor deze filters.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((doc) => {
              const selected = selectedIds.has(doc.id);
              return (
                <div
                  key={doc.id}
                  className={`grid grid-cols-12 items-center px-3 py-2 text-xs hover:bg-gray-50 ${
                    selected ? "bg-[#0055FF]/5" : ""
                  }`}
                  data-testid={`document-hub-item-${doc.id}`}
                >
                  <div className="col-span-1">
                    <button
                      onClick={() => toggleSelected(doc.id)}
                      data-testid={`document-hub-select-${doc.id}`}
                    >
                      {selected
                        ? <CheckSquare className="w-4 h-4 text-[#0055FF]" />
                        : <Square className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                  <div className="col-span-2">
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        TYPE_COLORS[doc.type] || "text-gray-700 bg-gray-100"
                      }`}
                    >
                      {TYPE_ICONS[doc.type]}
                      {doc.type_label}
                    </span>
                  </div>
                  <div className="col-span-3 font-mono truncate" title={doc.title}>
                    {doc.number}
                  </div>
                  <div className="col-span-2 truncate" title={doc.customer_name}>
                    {doc.customer_name}
                  </div>
                  <div className="col-span-1 text-center">
                    <span className="text-[10px] uppercase font-medium text-gray-500">
                      {doc.status_label}
                    </span>
                  </div>
                  <div className="col-span-1 text-right text-[10px] text-gray-500 font-mono">
                    {fmtDate(doc.created_at)}
                  </div>
                  <div className="col-span-2 flex justify-end gap-1">
                    {canPreview && (
                      <button
                        onClick={() => setPreviewDoc(doc)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Preview"
                        data-testid={`document-hub-preview-${doc.id}`}
                      >
                        <Eye className="w-3.5 h-3.5 text-gray-600" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDownloadOne(doc)}
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Downloaden"
                      data-testid={`document-hub-download-${doc.id}`}
                    >
                      <Download className="w-3.5 h-3.5 text-[#0055FF]" />
                    </button>
                    {canRegenerate && (doc.type === "packing_list" || doc.type === "booking_form" || doc.type.startsWith("manifest_")) && (
                      <button
                        onClick={() => handleRegenerate(doc)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Opnieuw genereren"
                        data-testid={`document-hub-regenerate-${doc.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 text-amber-600" />
                      </button>
                    )}
                    {canSend && (doc.type === "packing_list" || doc.type === "booking_form") && (
                      <button
                        onClick={() => handleResend(doc)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Opnieuw verzenden"
                        data-testid={`document-hub-resend-${doc.id}`}
                      >
                        <Send className="w-3.5 h-3.5 text-emerald-700" />
                      </button>
                    )}
                    {(doc.shipment_id || doc.consolidation_id) && (
                      <button
                        onClick={() => openShipment(doc)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Open zending/consolidatie"
                        data-testid={`document-hub-open-shipment-${doc.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-gray-500" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Paginatie */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 text-xs">
            <span className="text-gray-500">
              {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} van {total}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1 disabled:opacity-30"
                data-testid="document-hub-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2 py-0.5 text-[10px] font-bold">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-1 disabled:opacity-30"
                data-testid="document-hub-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Preview modal */}
      {previewDoc && (
        <DocumentPreviewModal
          document={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {/* Bulk-email preview dialog */}
      {showBulkEmailDialog && (
        <BulkEmailPreviewDialog
          documentIds={Array.from(selectedIds)}
          onClose={() => setShowBulkEmailDialog(false)}
          onSent={() => {
            setSelectedIds(new Set());
            setShowBulkEmailDialog(false);
            load();
          }}
        />
      )}

      {/* P0.19 SPRINT — canonical shipment lookup dialog. Vindt elke W-nummer
          ongeacht filter/consolidatie/archief. Opent volledig ShipmentDetail. */}
      {lookupOpen && (
        <ShipmentLookupDialog
          onClose={() => setLookupOpen(false)}
          onOpenShipment={(ship) => {
            if (ship?.tracking_number) {
              window.open(
                `/admin?section=warehouse&w=${encodeURIComponent(ship.tracking_number)}`,
                "_blank"
              );
            }
          }}
        />
      )}
    </div>
  );
}
